import { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { useLookupCompanyByNip } from "hooks/companies";
import type { CompanyLookupResultType } from "hooks/companies";
import { isValidPolishNip } from "utils/validation/nipValidation";

interface NipAutofillFields {
  name?: string;
  address?: string;
  postalCode?: string;
  city?: string;
}

interface NipAutofillProps {
  nipFieldName?: string;
  fields: NipAutofillFields;
  enabled?: boolean;
  onLoadingChange?: (isLoading: boolean) => void;
}

const MIN_LOOKUP_SPINNER_MS = 1000;

const NipAutofill: React.FC<NipAutofillProps> = ({
  nipFieldName = "nip",
  fields,
  enabled = true,
  onLoadingChange,
}) => {
  const { values, initialValues, setFieldValue } =
    useFormikContext<Record<string, any>>();
  const lastLookedUpRef = useRef<string | null>(null);
  const { mutateAsync: lookupCompany } = useLookupCompanyByNip();

  const rawNip = values?.[nipFieldName];
  const rawInitialNip = initialValues?.[nipFieldName];

  useEffect(() => {
    const normalize = (value: unknown) =>
      typeof value === "string" ? value.replace(/\D/g, "") : "";

    const nip = normalize(rawNip);
    const initialNip = normalize(rawInitialNip);

    if (!enabled || !isValidPolishNip(nip)) {
      lastLookedUpRef.current = null;
      return;
    }
    if (nip === initialNip) {
      lastLookedUpRef.current = nip;
      return;
    }
    if (lastLookedUpRef.current === nip) return;

    const timeout = setTimeout(async () => {
      lastLookedUpRef.current = nip;
      const startTime = Date.now();
      onLoadingChange?.(true);

      let data: CompanyLookupResultType | null = null;
      try {
        data = await lookupCompany(nip);
      } catch {
        // Błąd wyszukiwania NIP jest celowo ignorowany — brak onError,
        // tak jak poprzednio przy użyciu mutate().
      }

      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_LOOKUP_SPINNER_MS) {
        await new Promise((resolve) =>
          setTimeout(resolve, MIN_LOOKUP_SPINNER_MS - elapsed),
        );
      }

      if (data) {
        if (fields.name) setFieldValue(fields.name, data.name);
        if (fields.address && data.address) {
          setFieldValue(fields.address, data.address);
        }
        if (fields.postalCode && data.postalCode) {
          setFieldValue(fields.postalCode, data.postalCode);
        }
        if (fields.city && data.city) setFieldValue(fields.city, data.city);
      }

      onLoadingChange?.(false);
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawNip, rawInitialNip, enabled]);

  return null;
};

export default NipAutofill;
